import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Copy } from "@phosphor-icons/react";
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const INK = '#2A1E5C';
const INK2 = '#5D5A6B';
const GOLD = '#F5CB5C';

export default function BetaOpenModal({ isOpen, onClose, full, seatInfo }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    if (full) setIsFull(true);
  }, [full]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy || isFull) return;
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes('@') || !clean.includes('.')) {
      toast.error('Add your email so we can send your code.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/beta-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean, how_heard: 'homepage', agreed_terms: true })
      });
      const data = await res.json();
      if (res.status === 409 && data?.detail?.code === 'beta_full') {
        setIsFull(true);
        toast.error(data.detail.message);
        return;
      }
      if (!res.ok) {
        toast.error(data?.detail?.[0]?.msg || data?.detail || "Couldn't sign up, please try again.");
        return;
      }
      setSuccess(data);
      toast.success('Code sent to your inbox.');
    } catch {
      toast.error('Network hiccup. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!success?.promo_code) return;
    try {
      await navigator.clipboard.writeText(success.promo_code);
      toast.success('Code copied');
    } catch {
      toast.error("Couldn't copy, long-press to select");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
      data-testid="beta-open-modal"
    >
      <div
        className="relative w-full max-w-md my-8 p-7 sm:p-9 rounded-3xl shadow-2xl"
        style={{ background: '#FDFBF7', border: '1px solid rgba(42, 30, 92, 0.12)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:bg-black/5"
          style={{ color: INK }}
          aria-label="Close"
          data-testid="beta-modal-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {isFull ? (
          <div data-testid="beta-modal-full">
            <h2 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              The beta is closed for now.
            </h2>
            <p className="text-sm leading-relaxed mb-7" style={{ color: INK2 }}>
              All {seatInfo?.cap || 50} participation seats are taken. We will open the next window shortly.
            </p>
            <button
              onClick={onClose}
              className="w-full py-3 rounded-full font-bold text-sm transition-transform hover:scale-[1.01]"
              style={{ background: INK, color: '#FFFFFF' }}
            >
              Close
            </button>
          </div>
        ) : success ? (
          <div data-testid="beta-modal-success">
            <h2 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              Code sent to your inbox.
            </h2>
            <p className="text-sm leading-relaxed mb-6" style={{ color: INK2 }}>
              Apply it when you continue after your free sprint to lock in 50% off for life.
            </p>
            {success.promo_code && (
              <div className="flex items-center gap-2 mb-7">
                <div
                  className="flex-1 px-5 py-3 rounded-xl font-mono text-lg font-bold tracking-widest text-center"
                  style={{ background: '#F5F0E8', color: INK }}
                  data-testid="beta-modal-promo-code"
                >
                  {success.promo_code}
                </div>
                <button
                  type="button"
                  onClick={copyCode}
                  className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl font-bold text-sm transition-transform hover:scale-[1.02]"
                  style={{ background: GOLD, color: INK }}
                  data-testid="beta-modal-copy-btn"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full py-3 rounded-full font-bold text-sm transition-transform hover:scale-[1.01]"
              style={{ background: INK, color: '#FFFFFF' }}
              data-testid="beta-modal-done-btn"
            >
              Done
            </button>
          </div>
        ) : (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: '#B8860B' }}>
              Beta Access
            </p>
            <h2 className="text-2xl sm:text-3xl mb-3 leading-snug" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              Join the private beta.
            </h2>
            <p className="text-sm leading-relaxed mb-6" style={{ color: INK2 }}>
              In exchange for your feedback, get 50% off for life.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="beta-modal-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                aria-label="Email address"
                className="w-full px-5 py-3.5 rounded-full text-sm focus:outline-none transition-all"
                style={{ background: '#FFFFFF', border: '1px solid rgba(42, 30, 92, 0.18)', color: INK }}
                data-testid="beta-email-input"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full py-3.5 rounded-full font-bold text-sm transition-transform hover:scale-[1.01] disabled:opacity-60"
                style={{ background: GOLD, color: INK }}
                data-testid="beta-submit"
              >
                {busy ? 'Sending…' : 'Sign Up'}
              </button>
            </form>

            <p className="text-[11px] leading-relaxed mt-5" style={{ color: '#8B7E6A' }} data-testid="beta-modal-disclaimer">
              By signing up you agree to our{' '}
              <Link to="/terms" className="underline" style={{ color: INK }}>Terms</Link> and{' '}
              <Link to="/privacy" className="underline" style={{ color: INK }}>Privacy Policy</Link>.
              Payday Relay is an educational planning system, not financial, tax, legal, or investment advice.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
